import React from "react";
import {
  Route,
  Switch,
  Redirect,
  withRouter,
} from "react-router-dom";
import classnames from "classnames";
import {Box, IconButton, Link, CircularProgress, Typography as MuiTypography} from '@material-ui/core'
import Icon from '@mdi/react'

//icons
import {
  mdiFacebook as FacebookIcon,
  mdiTwitter as TwitterIcon,
  mdiGithub as GithubIcon,
} from '@mdi/js'

// styles
import useStyles from "./styles";

// components
import Header from "../Header";
import Sidebar from "../Sidebar";
import JoinGroupDialog from "../JoinGroupDialog";
import { Button } from "../Wrappers";

// pages
import Dashboard from "../../pages/dashboard";
import Fixtures from "../../pages/fixtures";
import Leaderboards from "../../pages/leaderboards";
import Calendar from "../../pages/calendar";
import Admin from "../../pages/admin";
import Typography from "../../pages/typography";
import Notifications from "../../pages/notifications";
import Maps from "../../pages/maps";
import Tables from "../../pages/tables";
import Icons from "../../pages/icons";
import Charts from "../../pages/charts";

// context
import { useLayoutState } from "../../context/LayoutContext";
import { useGroupState } from "../../context/GroupContext";

// localization
import { useTranslation, useLeagueNames } from "../../localization";

// A full-screen centered message used while groups load, on error, and for
// users that don't belong to any group.
function CenteredMessage({ children }) {
  return (
    <Box
      width="100vw"
      height="100vh"
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
    >
      {children}
    </Box>
  );
}

function Layout(props) {
  var classes = useStyles();
  var t = useTranslation();
  var leagueOverrides = useLeagueNames();

  // global
  var layoutState = useLayoutState();
  var { status, leagues, leagueNames, selectedGroup, isAdmin } = useGroupState();

  // local
  var [joinOpen, setJoinOpen] = React.useState(false);

  // Until we know which groups (and therefore leagues) the user has, there's
  // nothing meaningful to route to, so we gate the whole app on group status.
  if (status === "loading") {
    return (
      <CenteredMessage>
        <CircularProgress />
      </CenteredMessage>
    );
  }
  if (status === "error") {
    return (
      <>
        <Header history={props.history} />
        <CenteredMessage>
          <MuiTypography variant="h5">
            {t.layout.groupsError}
          </MuiTypography>
        </CenteredMessage>
      </>
    );
  }
  if (status === "none") {
    return (
      <>
        <Header history={props.history} />
        <CenteredMessage>
          <MuiTypography variant="h4" gutterBottom>
            {t.layout.noGroupsTitle}
          </MuiTypography>
          <MuiTypography variant="body1" color="textSecondary">
            {t.layout.noGroupsBody}
          </MuiTypography>
          <Box mt={3}>
            <Button
              variant="contained"
              color="primary"
              onClick={() => setJoinOpen(true)}
            >
              {t.layout.joinGroup}
            </Button>
          </Box>
        </CenteredMessage>
        <JoinGroupDialog
          open={joinOpen}
          onClose={() => setJoinOpen(false)}
          onJoined={() => props.history.push("/app/fixtures")}
        />
      </>
    );
  }

  var firstLeague = leagues[0];

  return (
    <div className={classes.root}>
        <>
          <Header history={props.history} />
          <Sidebar />
          <div
            className={classnames(classes.content, {
              [classes.contentShift]: layoutState.isSidebarOpened,
            })}
          >
            <div className={classes.fakeToolbar} />
            <Switch>
              <Route path="/app/dashboard" component={Dashboard} />
              <Route exact path="/app/fixtures" render={() => (
                <Redirect to={"/app/fixtures/" + firstLeague} />
              )} />
              <Route path="/app/fixtures/:league" render={(routeProps) => {
                var league = routeProps.match.params.league;
                // A league the current group doesn't have (e.g. a stale
                // bookmark or after switching groups) falls back to the
                // group's first league.
                if (!leagues.includes(league)) {
                  return <Redirect to={"/app/fixtures/" + firstLeague} />;
                }
                return (
                  <Fixtures
                    {...routeProps}
                    title={leagueOverrides[league] || leagueNames[league] || league}
                    league={league}
                    // Remount on group change too: predictions differ per group
                    // and the prediction inputs are uncontrolled (defaultValue),
                    // so they only pick up new values on a fresh mount.
                    key={selectedGroup + ":" + league}
                  />
                );
              }} />
              <Route exact path="/app/leaderboards" render={() => (
                <Redirect to="/app/leaderboards/aggregate" />
              )} />
              <Route path="/app/leaderboards/:view" render={(routeProps) => {
                var view = routeProps.match.params.view;
                // "aggregate" or a league the current group has; a stale slug
                // (e.g. after switching groups) falls back to the aggregate.
                if (view !== "aggregate" && !leagues.includes(view)) {
                  return <Redirect to="/app/leaderboards/aggregate" />;
                }
                return (
                  <Leaderboards
                    {...routeProps}
                    view={view}
                    // Remount on group change so standings refetch for the
                    // newly selected group.
                    key={selectedGroup}
                  />
                );
              }} />
              <Route exact path="/app/calendar" render={() => (
                <Redirect to="/app/calendar/today" />
              )} />
              <Route path="/app/calendar/:day" render={(routeProps) => (
                // Out-of-window or unknown day slugs are normalized to Today inside
                // the component. Remount on group change to refetch.
                <Calendar
                  {...routeProps}
                  day={routeProps.match.params.day}
                  key={selectedGroup}
                />
              )} />
              <Route path="/app/admin" render={(routeProps) =>
                isAdmin ? (
                  <Admin {...routeProps} key={selectedGroup} />
                ) : (
                  <Redirect to="/app/fixtures" />
                )
              } />
              <Route path="/app/typography" component={Typography} />
              <Route path="/app/tables" component={Tables} />
              <Route path="/app/notifications" component={Notifications} />
              <Route
                exact
                path="/app/ui"
                render={() => <Redirect to="/app/ui/icons" />}
              />
              <Route path="/app/ui/maps" component={Maps} />
              <Route path="/app/ui/icons" component={Icons} />
              <Route path="/app/ui/charts" component={Charts} />
            </Switch>
            {/* <Box
              mt={5}
              width={"100%"}
              display={"flex"}
              alignItems={"center"}
              justifyContent="space-between"
            >
              <div>
                <Link
                  color={'primary'}
                  href={'https://flatlogic.com/'}
                  target={'_blank'}
                  className={classes.link}
                >
                  Flatlogic
                </Link>
                <Link
                  color={'primary'}
                  href={'https://flatlogic.com/about'}
                  target={'_blank'}
                  className={classes.link}
                >
                  About Us
                </Link>
                <Link
                  color={'primary'}
                  href={'https://flatlogic.com/blog'}
                  target={'_blank'}
                  className={classes.link}
                >
                  Blog
                </Link>
              </div>
              <div>
                <Link
                  href={'https://www.facebook.com/flatlogic'}
                  target={'_blank'}
                >
                  <IconButton aria-label="facebook">
                    <Icon
                      path={FacebookIcon}
                      size={1}
                      color="#6E6E6E99"
                    />
                  </IconButton>
                </Link>
                <Link
                  href={'https://twitter.com/flatlogic'}
                  target={'_blank'}
                >
                  <IconButton aria-label="twitter">
                    <Icon
                      path={TwitterIcon}
                      size={1}
                      color="#6E6E6E99"
                    />
                  </IconButton>
                </Link>
                <Link
                  href={'https://github.com/flatlogic'}
                  target={'_blank'}
                >
                  <IconButton
                    aria-label="github"
                    style={{marginRight: -12}}
                  >
                    <Icon
                      path={GithubIcon}
                      size={1}
                      color="#6E6E6E99"
                    />
                  </IconButton>
                </Link>
              </div>
            </Box> */}
          </div>
        </>
    </div>
  );
}

export default withRouter(Layout);
